import React, { useState, useMemo, useEffect } from 'react';
import './Table.css';
import { TableDataAPI } from '../../../Api/TableDataAPI';
import TableImportPanel from './TableImportPanel';

const columns = [
  { key: 'bloque', label: 'Bloque' },
  { key: 'barco', label: 'Barco' },
  { key: 'lance', label: 'Lance' },
  { key: 'empresa', label: 'Empresa' },
  { key: 'id', label: 'ID Transaccion' },
  { key: 'piezas', label: 'Piezas' },
  { key: 'pesoKs', label: 'Peso (kg)' },
  { key: 'biomasa', label: 'Biomasa (T)' },
  { key: 'corral', label: 'Corral' },
];

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeRow = (item) => {
  const piezas = toNumber(item.piezas);
  const pesoKs = toNumber(item.pesoKs);
  const biomasa = item.biomasa === undefined || item.biomasa === null
    ? (piezas * pesoKs) / 1000
    : toNumber(item.biomasa);

  return {
    rowId: item.rowId,
    bloque: item.bloque || '',
    barco: item.barco || '',
    lance: item.lance || '',
    empresa: item.empresa || '',
    id: item.id || item.transactionId || '',
    piezas,
    pesoKs,
    biomasa,
    corral: item.corral || '',
  };
};

const Table = () => {
  const [rows, setRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState('');
  const [filters, setFilters] = useState({});
  const [globalSearch, setGlobalSearch] = useState('');
  const [sortConfig, setSortConfig] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [mobileSortKey, setMobileSortKey] = useState('id');
  const [canImport, setCanImport] = useState(false);
  const [selectedTargetUserId, setSelectedTargetUserId] = useState(null);
  const [importNotice, setImportNotice] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let mounted = true;

    setTableLoading(true);
    setTableError('');

    TableDataAPI.getRows(selectedTargetUserId)
      .then((response) => {
        if (!mounted) return;
        setRows(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error(error);
        if (!mounted) return;
        setRows([]);
        setTableError(error?.response?.data?.message || 'Could not load table rows.');
      })
      .finally(() => {
        if (mounted) setTableLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [selectedTargetUserId, refreshToken]);

  useEffect(() => {
    let mounted = true;

    TableDataAPI.getImportStatus()
      .then((response) => {
        if (mounted) setCanImport(Boolean(response.data?.canImport));
      })
      .catch((error) => {
        console.error(error);
        if (mounted) setCanImport(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleFilterChange = (col, val) => {
    setFilters(prev => ({ ...prev, [col]: val.toLowerCase() }));
  };

  const handleGlobalSearch = (e) => {
    setGlobalSearch(e.target.value.toLowerCase());
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const processedData = useMemo(() => {
    let data = rows.map(normalizeRow);

    if (globalSearch) {
      data = data.filter(item =>
        columns.some(col => String(item[col.key]).toLowerCase().includes(globalSearch))
      );
    }

    data = data.filter(item =>
      Object.keys(filters).every(key =>
        String(item[key]).toLowerCase().includes(filters[key])
      )
    );

    return data;
  }, [rows, filters, globalSearch]);

  const effectiveSortConfig = useMemo(() => {
    if (sortConfig) return sortConfig;
    if (mobileSortKey) return { key: mobileSortKey, direction: 'asc' };
    return null;
  }, [sortConfig, mobileSortKey]);

  const sortedData = useMemo(() => {
    const dataToSort = [...processedData];
    if (!effectiveSortConfig) return dataToSort;

    const { key: sortKey, direction } = effectiveSortConfig;

    dataToSort.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return dataToSort;
  }, [processedData, effectiveSortConfig]);

  const totals = useMemo(() => sortedData.reduce(
    (acc, curr) => ({
      piezas: acc.piezas + Number(curr.piezas),
      biomasa: acc.biomasa + curr.biomasa,
    }),
    { piezas: 0, biomasa: 0 }
  ), [sortedData]);

  useEffect(() => {
    setExpandedRow(null);
  }, [globalSearch, filters, sortConfig, mobileSortKey, selectedTargetUserId]);

  const toggleRow = (rowKey) => {
    setExpandedRow(prev => (prev === rowKey ? null : rowKey));
  };

  const handleTargetUserChange = (targetUserId) => {
    setSelectedTargetUserId(targetUserId);
    setImportNotice('');
  };

  const handleImportSuccess = (result) => {
    const targetUserId = Number(result.targetUserId);
    setSelectedTargetUserId(targetUserId);
    setImportNotice(`${result.rowCount} rows appended for user #${targetUserId}.`);
    setRefreshToken(prev => prev + 1);
  };

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="title-section">
          <h1>TABLA MAESTRA <span className="highlight">LANCES</span></h1>
          <p className="subtitle">Control de Biomasa y Captura Operativa</p>
        </div>

        <div className="summary-cards">
          <div className="card">
            <div className="card-label">Total Piezas</div>
            <div className="card-value">{totals.piezas.toLocaleString('hr-HR')}</div>
          </div>
          <div className="card highlight-card">
            <div className="card-label">Total Biomasa (T)</div>
            <div className="card-value">
              {totals.biomasa.toLocaleString('hr-HR', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </header>

      {canImport && (
        <>
          <TableImportPanel
            onImportSuccess={handleImportSuccess}
            onTargetUserChange={handleTargetUserChange}
          />
          {importNotice && <div className="table-import-banner">{importNotice}</div>}
        </>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search globally"
          value={globalSearch}
          onChange={handleGlobalSearch}
          className="global-search"
        />
      </div>

      <div className="mobile-controls">
        <select value={mobileSortKey} onChange={(event) => setMobileSortKey(event.target.value)}>
          {columns.map((column) => (
            <option key={column.key} value={column.key}>
              Sort by {column.label}
            </option>
          ))}
        </select>
      </div>

      <div className="table-wrapper">
        {tableLoading ? (
          <div className="empty-state table-state-message">
            <p>Loading rows...</p>
          </div>
        ) : tableError ? (
          <div className="empty-state table-state-error">
            <p>{tableError}</p>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="empty-state">
            <p>Nema rezultata</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.key}>
                    <div onClick={() => handleSort(col.key)} className="sort-label">
                      {col.label} {sortConfig?.key === col.key ? (sortConfig.direction === 'asc' ? '^' : 'v') : ''}
                    </div>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder="Filter"
                      onChange={e => handleFilterChange(col.key, e.target.value)}
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedData.map((row, i) => {
                const rowKey = row.rowId || `${row.id}-${i}`;
                const isOpen = expandedRow === rowKey;

                return (
                  <React.Fragment key={rowKey}>
                    <tr className="row-item desktop-row">
                      <td data-label="Bloque"><span className="badge-bloque">{row.bloque}</span></td>
                      <td data-label="Barco" className="text-bold">{row.barco}</td>
                      <td data-label="Lance"><span className="badge-lance">{row.lance}</span></td>
                      <td data-label="Empresa"><span className="badge-empresa">{row.empresa}</span></td>
                      <td data-label="ID Transaccion" className="text-id">{row.id}</td>
                      <td data-label="Piezas" className="text-bold">{Number(row.piezas).toLocaleString('hr-HR')}</td>
                      <td data-label="Peso (kg)">{row.pesoKs} kg</td>
                      <td data-label="Biomasa (T)" className="text-biomasa">
                        {row.biomasa.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td data-label="Corral"><span className="badge-corral">{row.corral}</span></td>
                    </tr>

                    <tr className="mobile-row-id">
                      <td className="mobile-only-cell" colSpan={columns.length}>
                        <button
                          type="button"
                          className="mobile-id-button"
                          onClick={() => toggleRow(rowKey)}
                          aria-expanded={isOpen}
                        >
                          <span className="mobile-id-label">ID Transaccion</span>
                          <span className="mobile-id-value">{row.id}</span>
                          <span className="mobile-id-chevron">{isOpen ? '^' : 'v'}</span>
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className={`mobile-row-details ${isOpen ? 'open' : ''}`}>
                        <td className="mobile-only-cell" colSpan={columns.length}>
                          <div className="mobile-details">
                            <div className="mobile-detail"><span>Bloque</span><strong>{row.bloque}</strong></div>
                            <div className="mobile-detail"><span>Barco</span><strong>{row.barco}</strong></div>
                            <div className="mobile-detail"><span>Lance</span><strong>{row.lance}</strong></div>
                            <div className="mobile-detail"><span>Empresa</span><strong>{row.empresa}</strong></div>
                            <div className="mobile-detail"><span>Piezas</span><strong>{Number(row.piezas).toLocaleString('hr-HR')}</strong></div>
                            <div className="mobile-detail"><span>Peso (kg)</span><strong>{row.pesoKs} kg</strong></div>
                            <div className="mobile-detail"><span>Biomasa (T)</span><strong>{row.biomasa.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                            <div className="mobile-detail"><span>Corral</span><strong>{row.corral}</strong></div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Table;
